import {checkIncluded, findSection} from 'ducks/reportSections';
import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';
import {comparableListingUpdateFields, getDefaultDescription, getIsFieldVisible, MASK_VALUE} from 'lib/PropertyUtils';
import RapidSearch from '../constants/rapidSearch';

export const distanceKm = (latLng1, latLng2) => {
  const earthRadiusKm = 6371;
  const lat1Rad = latLng1.lat * Math.PI / 180;
  const lat2Rad = latLng2.lat * Math.PI / 180;
  const deltaLatRad = (latLng2.lat - latLng1.lat) * Math.PI / 180;
  const deltaLngRad = (latLng2.lng - latLng1.lng) * Math.PI / 180;

  const a = Math.sin(deltaLatRad / 2) * Math.sin(deltaLatRad / 2) +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) *
    Math.sin(deltaLngRad / 2) * Math.sin(deltaLngRad / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadiusKm * c;
};

export const fitBounds = ({
  latLng1,
  latLng2,
  dimensions: {width, height},
  targetPixelsBetweenPoints,
  padding: {
    left = 0,
    bottom = 0,
  } = {},
}) => {
  const distanceFloor = 0.1; // 100 meters, a very small distance

  const latMin = Math.min(latLng1.lat, latLng2.lat);
  const lngMin = Math.min(latLng1.lng, latLng2.lng);
  const latMax = Math.max(latLng1.lat, latLng2.lat);
  const lngMax = Math.max(latLng1.lng, latLng2.lng);

  const midLat = (latLng1.lat + latLng2.lat) / 2;
  const midLng = (latLng1.lng + latLng2.lng) / 2;
  const kmx = distanceKm({lat: midLat, lng: lngMin}, {lat: midLat, lng: lngMax});
  const kmy = distanceKm({lat: latMin, lng: midLng}, {lat: latMax, lng: midLng});
  const targetKilometersPerPx = Math.max(kmx, kmy, distanceFloor) / targetPixelsBetweenPoints; // distanceFloor prevents later division by zero

  const zoom = Math.floor(Math.log(156.54303392 * Math.cos(midLat * Math.PI / 180) / targetKilometersPerPx) / Math.log(2));
  const kilometersPerPx = 156.54303392 * Math.cos(midLat * Math.PI / 180) / Math.pow(2, zoom);

  const lngPerKilometer = 1 / distanceKm({lat: midLat, lng: midLng}, {lat: midLat, lng: midLng + 1});
  const latPerKilometer = 1 / distanceKm({lat: midLat, lng: midLng}, {lat: midLat + 1, lng: midLng});
  const widthPadding = (width - left - Math.max(kmx, kmy) / kilometersPerPx) / 2;
  const heightPadding = (height - bottom - Math.max(kmx, kmy) / kilometersPerPx) / 2;
  const centerLng = lngMin - (left - width / 2.0 + widthPadding) * kilometersPerPx * lngPerKilometer;
  const centerLat = latMin - (bottom - height / 2.0 + heightPadding) * kilometersPerPx * latPerKilometer;

  return {
    zoom,
    center: {lat: centerLat, lng: centerLng},
  };
};

export const latLngToPoint = (latLng, center, zoom, size) => {
  const kilometersPerPx = 156.54303392 * Math.cos(center.lat * Math.PI / 180) / Math.pow(2, zoom);
  const kilometerPerLat = distanceKm({lat: center.lat, lng: center.lng}, {lat: center.lat + 1, lng: center.lng});
  const kilometerPerLng = distanceKm({lat: center.lat, lng: center.lng}, {lat: center.lat, lng: center.lng + 1});
  return {
    x: -(center.lng - latLng.lng) * kilometerPerLng / kilometersPerPx + size.x / 2,
    y: (center.lat - latLng.lat) * kilometerPerLat / kilometersPerPx + size.y / 2,
  };
};

export function getPropertyPrice(property, comparableType) {
  if (comparableType === RapidSearch.COMPARABLE_TYPE.SALES) {
    return property.salePrice;
  } else if (comparableType === RapidSearch.COMPARABLE_TYPE.LISTINGS) {
    if (getIsFieldVisible(comparableListingUpdateFields[0], property.displayFields)) {
      return property[comparableListingUpdateFields[0]];
    }
    return getDefaultDescription();
  } else if (comparableType === RapidSearch.COMPARABLE_TYPE.RENTALS) {
    const {salePrice, rentalLastCampaignLastListedPeriod} = property || {};
    return salePrice && salePrice !== MASK_VALUE
        ? `$${salePrice}${rentalLastCampaignLastListedPeriod ? `/${rentalLastCampaignLastListedPeriod}` : ''}`
        : MASK_VALUE;
  }
}

export function getPropertyPriceIsEdited(property, comparableType) {
  switch (comparableType) {
    case 'sales':
      return property.salePriceIsEdited;
    case 'listings':
      const listingPriceField = comparableListingUpdateFields[0];
      const isFieldVisible = getIsFieldVisible(listingPriceField, property.displayFields);
      return isFieldVisible && property[`${listingPriceField}IsEdited`];
    default:
      return false;
  }
}

export function parseListingPriceAmount(property) {
  const listingPrice = property[comparableListingUpdateFields[0]];
  if (!listingPrice) {
    return null;
  }

  const regex = /^.*?\$([0-9.,]+)([mk]?).*$/i;
  const matches = regex.exec(listingPrice);
  if (matches && matches.length > 0) {
    const amount = parseFloat(matches[1].replace(/,/g, ''));
    const multiplier = matches[2].toUpperCase();
    if (multiplier === 'M') {
      return amount * 1e6;
    } else if (multiplier === 'K') {
      return amount * 1e3;
    }
    return amount;
  }
  return '';
}

export const getSchoolMarkers = (state) => {
  const schoolData = state.propertyDetail.suburbInsights.schoolData;

  return schoolData.filter(s => !!s.latitude && !!s.longitude).map( s => {
    return {
      type: s.insideSchoolCatchment ? 'inside': 'outside' ,
      lat: s.latitude,
      lng: s.longitude,
    };
  });
};

export const getMarkers = (state, comparableType) => {
  const reportType = state.reportType.value;
  const mapSection = findSection(state.reportSections, reportType, 'COMPARABLE_MAP');
  const hasComparableMaps = checkIncluded(mapSection, state);
  const itemMapSection = findSection(state.reportSections, reportType, `COMPARABLE_${comparableType.toUpperCase()}_MAP`);
  const hasComparableItemsMap = checkIncluded(itemMapSection, state);

  if (!hasComparableMaps && !hasComparableItemsMap) {
    return [];
  }

  const comparableItems = state[`comparable${capitalize(comparableType)}`];

  return comparableItems.selected
    .filter(id => comparableItems.properties[id].latitude && comparableItems.properties[id].longitude)
    .filter((id, i) => hasComparableMaps || i < 10)
    .map((id, i) => {
      const property = comparableItems.properties[id];
      return {
        id,
        type: comparableType,
        label: i < (hasComparableMaps ? 9999 : 10) ? (i + 1).toString() : null,
        lat: property?.latitude,
        lng: property?.longitude,
        sla: property.singleLineAddress,
        beds: property.bedrooms,
        bedsIsEdited: property.bedroomsIsEdited,
        baths: property.bathrooms,
        bathsIsEdited: property.bathroomsIsEdited,
        cars: property.carSpaces,
        carsIsEdited: property.carSpacesIsEdited,
        daysOnMarket: property.daysOnMarket,
        salePrice: getPropertyPrice(property, comparableType),
        salePriceIsEdited: getPropertyPriceIsEdited(property, comparableType)
      };
    });
};

export function checkIfMarkersChanged(newMarkers, oldMarkers) {
  const newMappedMarkers = newMarkers?.map(m => ({
    id: m.id,
    sla: m.sla,
    lat: m.lat,
    lng: m.lng,
  }));
  const oldMappedMarkers = oldMarkers?.map(m => ({
    id: m.id,
    sla: m.sla,
    lat: m.lat,
    lng: m.lng,
  }));
  return !isEqual(newMappedMarkers, oldMappedMarkers);
}
